import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ExcelJS from "exceljs";
import ConfirmModal from "../../common/ConfirmModal";
import Dropdown from "../../common/dropDown";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import TextEditor from "../../common/TextEditor/TextEditor";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import FaqAnswerContent from "./FaqAnswerContent";
import {
  deleteAllPlatformFaqs,
  deletePlatformFaq,
  deletePlatformUserGuide,
  fetchPlatformFaqs,
  fetchPlatformUserGuideFile,
  fetchPlatformUserGuides,
  cloneFaqNested,
  groupFaqsNested,
  importPlatformFaqs,
  renumberAllFaqs,
  reorderPlatformFaqs,
  reorderPlatformUserGuides,
  savePlatformFaq,
  savePlatformUserGuide,
} from "../../../services/faqService";
import { resolvePlatformSuperAdminStaffId } from "../../../utils/permissionUtils";

const FAQ_EXCEL_HEADERS = [
  "SortOrder",
  "MainModule",
  "SubModule",
  "Question",
  "Answer",
  "VideoLink",
];

/** Plain-text cap for FAQ answers (HTML markup not counted). */
const ANSWER_MAX_LENGTH = 4000;
const CREATE_MAIN_MODULE_VALUE = "__create_new_main__";
const CREATE_MAIN_MODULE_OPTION = {
  value: CREATE_MAIN_MODULE_VALUE,
  label: "+ Create new main module",
};
const CREATE_SUB_MODULE_VALUE = "__create_new_sub__";
const CREATE_SUB_MODULE_OPTION = {
  value: CREATE_SUB_MODULE_VALUE,
  label: "+ Create new sub module",
};
const CREATE_GUIDE_MODULE_VALUE = "__create_new_guide_module__";
const CREATE_GUIDE_MODULE_OPTION = {
  value: CREATE_GUIDE_MODULE_VALUE,
  label: "+ Create new module",
};

const EMPTY_FORM = {
  faqId: "",
  mainModule: "",
  subModule: "",
  question: "",
  answer: "",
  videoLink: "",
};

const KEYWORD_LIMIT = 20;
const SUGGESTION_DELAY = 200;

function toPlainText(value) {
  return String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isAnswerEmpty(html) {
  return !toPlainText(html);
}

function validateAnswerLength(html) {
  const len = toPlainText(html).length;
  if (len > ANSWER_MAX_LENGTH) {
    return {
      allowed: false,
      message: `Answer cannot exceed ${ANSWER_MAX_LENGTH.toLocaleString()} characters.`,
    };
  }
  return { allowed: true };
}

function isValidGuideUrl(value) {
  try {
    const url = new URL(String(value || "").trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function inferGuideKind(url) {
  const path = String(url || "").split("?")[0].toLowerCase();
  if (path.endsWith(".pdf")) return "pdf";
  if (path.endsWith(".html") || path.endsWith(".htm")) return "html";
  return "link";
}

function formatGuideHost(url) {
  try {
    return new URL(String(url || "").trim()).hostname.replace(/^www\./, "");
  } catch {
    return String(url || "").trim() || "Document link";
  }
}

function findGuideByModule(guides, moduleName) {
  const key = String(moduleName || "").trim().toLowerCase();
  if (!key) return null;
  return (
    (guides || []).find(
      (g) =>
        String(g.moduleKey || "")
          .toLowerCase() === key ||
        String(g.moduleName || "")
          .toLowerCase() === key,
    ) || null
  );
}

const DEFAULT_GUIDE_MODULES = ["eISF", "QMS"];
const EMPTY_GUIDE_FORM = { moduleName: "", documentUrl: "" };

/**
 * Super Admin FAQ manager: CRUD + Excel import/export (no cross-DB deploy).
 * Main list reuses the public FAQ page layout (sidebar + accordion) with
 * native drag-and-drop reordering. Use asPage on /superadmin/faq-management;
 * modal mode kept for reuse.
 */
function ManageFaqModal({
  open,
  onClose,
  userData,
  onFaqsChanged,
  asPage = false,
  pageBanner = null,
}) {
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const staffId = resolvePlatformSuperAdminStaffId(userData) || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formOpen, setFormOpen] = useState(false);
  const [creatingMainModule, setCreatingMainModule] = useState(false);
  const [creatingSubModule, setCreatingSubModule] = useState(false);

  const [activeMainModule, setActiveMainModule] = useState("");
  const [activeSubModule, setActiveSubModule] = useState("");
  const [expandedDropdown, setExpandedDropdown] = useState("");
  const [expandedItems, setExpandedItems] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const [dragFaqId, setDragFaqId] = useState(null);
  const [dropTargetFaqId, setDropTargetFaqId] = useState(null);
  const [dragSubName, setDragSubName] = useState(null);
  const [dropTargetSubName, setDropTargetSubName] = useState(null);
  const [dragMainName, setDragMainName] = useState(null);
  const [dropTargetMainName, setDropTargetMainName] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    kind: null,
    id: null,
  });
  const [excelUploadOpen, setExcelUploadOpen] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelPreviewRows, setExcelPreviewRows] = useState([]);
  const [excelParsing, setExcelParsing] = useState(false);
  const excelInputRef = useRef(null);

  const [guidesOpen, setGuidesOpen] = useState(false);
  const [guides, setGuides] = useState([]);
  const [guidesLoading, setGuidesLoading] = useState(false);
  const [guideSaving, setGuideSaving] = useState(false);
  const [guideForm, setGuideForm] = useState(EMPTY_GUIDE_FORM);
  const [guideConfirm, setGuideConfirm] = useState(null);
  const [creatingGuideModule, setCreatingGuideModule] = useState(false);
  const [guideModuleKey, setGuideModuleKey] = useState(0);
  const [guidePreview, setGuidePreview] = useState(null);
  const [guidePreviewLoading, setGuidePreviewLoading] = useState(false);
  const [dragGuideKey, setDragGuideKey] = useState(null);
  const [dropTargetGuideKey, setDropTargetGuideKey] = useState(null);
  const [guidesReordering, setGuidesReordering] = useState(false);
  const guidePreviewUrlRef = useRef(null);

  const loadFaqs = useCallback(async () => {
    setLoading(true);
    try {
      const { rows: list } = await fetchPlatformFaqs();
      setRows(list);
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Could not load FAQs.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  const loadGuides = useCallback(async () => {
    setGuidesLoading(true);
    try {
      const { guides: list } = await fetchPlatformUserGuides();
      setGuides(list);
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Could not load user guides.",
        type: "danger",
      });
    } finally {
      setGuidesLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setFormOpen(false);
    setExcelUploadOpen(false);
    setExcelFile(null);
    setExcelPreviewRows([]);
    setExcelParsing(false);
    setSearchQuery("");
    setShowSuggestions(false);
    loadFaqs();
    loadGuides();
  }, [open, loadFaqs, loadGuides]);

  /** Nested { Main: { Sub: [faqs] } } shape — mirrors public FAQ page sidebar. */
  const faqData = useMemo(() => groupFaqsNested(rows), [rows]);

  const categories = useMemo(() => {
    return Object.entries(faqData)
      .map(([id, subModules]) => ({
        id,
        label: id,
        subItems: Object.keys(subModules || {}),
      }))
      .filter((cat) => cat.subItems.length > 0);
  }, [faqData]);

  useEffect(() => {
    const mains = Object.keys(faqData);
    if (mains.length === 0) {
      setActiveMainModule("");
      setActiveSubModule("");
      setExpandedDropdown("");
      return;
    }
    setActiveMainModule((prev) => (prev && faqData[prev] ? prev : mains[0]));
    setExpandedDropdown((prev) => (prev && faqData[prev] ? prev : mains[0]));
  }, [faqData]);

  useEffect(() => {
    const subs = Object.keys(faqData[activeMainModule] || {});
    if (!subs.length) {
      setActiveSubModule("");
      return;
    }
    if (!subs.includes(activeSubModule)) {
      setActiveSubModule(subs[0]);
    }
  }, [faqData, activeMainModule, activeSubModule]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setExpandedItems(new Set());
    }
  }, [activeMainModule, activeSubModule, searchQuery]);

  useEffect(() => {
    if (!selectedVideo) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSelectedVideo(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selectedVideo]);

  /** Main modules in sidebar/FAQ order (SortOrder), not alphabetical. */
  const mainModules = useMemo(() => Object.keys(faqData), [faqData]);

  /** Module dropdown for User Guides: FAQ main modules + existing guides + defaults. */
  const guideModuleOptions = useMemo(() => {
    const seen = new Set();
    const ordered = [];
    const add = (name) => {
      const trimmed = String(name || "").trim();
      const key = trimmed.toLowerCase();
      if (!trimmed || seen.has(key)) return;
      seen.add(key);
      ordered.push(trimmed);
    };
    mainModules.forEach(add);
    guides.forEach((g) => add(g.moduleName || g.moduleKey));
    DEFAULT_GUIDE_MODULES.forEach(add);
    if (guideForm.moduleName && !creatingGuideModule) {
      add(guideForm.moduleName);
    }
    return ordered
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [mainModules, guides, guideForm.moduleName, creatingGuideModule]);

  const selectedGuideModuleOption = useMemo(() => {
    if (creatingGuideModule || !guideForm.moduleName) return null;
    return (
      guideModuleOptions.find((opt) => opt.value === guideForm.moduleName) || {
        value: guideForm.moduleName,
        label: guideForm.moduleName,
      }
    );
  }, [creatingGuideModule, guideForm.moduleName, guideModuleOptions]);

  const selectedModuleExistingGuide = useMemo(
    () => findGuideByModule(guides, guideForm.moduleName),
    [guides, guideForm.moduleName],
  );

  const keywords = useMemo(() => {
    const keywordSet = new Set();
    rows.forEach((faq) => {
      const words =
        `${faq.question} ${toPlainText(faq.answer)}`
          .toLowerCase()
          .match(/\b\w{3,}\b/g) || [];
      words.forEach((word) => keywordSet.add(word));
    });
    return Array.from(keywordSet).sort().slice(0, KEYWORD_LIMIT);
  }, [rows]);

  const filteredKeywords = useMemo(() => {
    if (!showSuggestions || !keywords.length) return [];
    const query = searchQuery.toLowerCase();
    return keywords
      .filter((keyword) => keyword.includes(query) && keyword !== query)
      .slice(0, 10);
  }, [keywords, searchQuery, showSuggestions]);

  /** When not searching this is exactly the reorderable list for the active submodule. */
  const filteredFAQs = useMemo(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return rows.filter(
        (faq) =>
          faq.question.toLowerCase().includes(query) ||
          toPlainText(faq.answer).toLowerCase().includes(query),
      );
    }
    return rows
      .filter(
        (faq) =>
          faq.mainModule === activeMainModule &&
          faq.subModule === activeSubModule,
      )
      .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
  }, [rows, searchQuery, activeMainModule, activeSubModule]);

  const categoryTitle = useMemo(() => {
    if (searchQuery.trim()) return "Search results";
    if (activeMainModule && activeSubModule)
      return `${activeMainModule} · ${activeSubModule}`;
    if (activeMainModule) return activeMainModule;
    return "Manage FAQs";
  }, [searchQuery, activeMainModule, activeSubModule]);

  // Keep grip icons usable while order saves in the background (train latency).
  const canDrag = !searchQuery.trim() && !saving && !loading;

  const toggleExpand = (faqId) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(faqId)) next.delete(faqId);
      else next.add(faqId);
      return next;
    });
  };

  const clearDrag = () => {
    setDragFaqId(null);
    setDropTargetFaqId(null);
  };

  const clearAllFaqDragState = () => {
    setDragFaqId(null);
    setDropTargetFaqId(null);
    setDragSubName(null);
    setDropTargetSubName(null);
    setDragMainName(null);
    setDropTargetMainName(null);
  };

  const handleDragStart = (event, faq) => {
    if (!canDrag || !faq.faqId) {
      event.preventDefault();
      return;
    }
    setDragFaqId(faq.faqId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", faq.faqId);
  };

  const handleDragOver = (event, faq) => {
    if (!canDrag || !dragFaqId || dragFaqId === faq.faqId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDropTargetFaqId(faq.faqId);
  };

  const persistFaqOrder = async (items, failureMessage) => {
    if (!staffId) {
      showAlert({
        title: "Reorder failed",
        message: "Super Admin StaffId not found in session.",
        type: "danger",
      });
      clearAllFaqDragState();
      return;
    }

    const previousItems = rows.map((row) => ({
      faqId: row.faqId,
      sortOrder: row.sortOrder,
    }));
    const sortOrderMap = new Map(
      items.map((item) => [item.faqId, item.sortOrder]),
    );
    // Optimistic UI — swap feels instant; save is one bulk call in background.
    setRows((prev) =>
      prev.map((row) =>
        sortOrderMap.has(row.faqId)
          ? { ...row, sortOrder: sortOrderMap.get(row.faqId) }
          : row,
      ),
    );
    clearAllFaqDragState();
    setReordering(true);
    try {
      await reorderPlatformFaqs({ staffId, items, previousItems });
    } catch (err) {
      showAlert({
        title: "Reorder failed",
        message: err?.message || failureMessage,
        type: "danger",
      });
      await loadFaqs();
    } finally {
      setReordering(false);
      clearAllFaqDragState();
    }
  };

  const handleDrop = async (event, targetFaq) => {
    event.preventDefault();
    const sourceId = dragFaqId;
    clearDrag();
    if (!canDrag || !sourceId || sourceId === targetFaq.faqId) return;

    const list = filteredFAQs;
    const fromIndex = list.findIndex((faq) => faq.faqId === sourceId);
    const toIndex = list.findIndex((faq) => faq.faqId === targetFaq.faqId);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

    // Reorder within active sub, then renumber ALL mains globally so
    // main / sub / question SortOrder bands stay consistent.
    const mainOrder = Object.keys(faqData);
    const nested = cloneFaqNested(faqData);
    const reorderedActiveSub = [...list];
    const [moved] = reorderedActiveSub.splice(fromIndex, 1);
    reorderedActiveSub.splice(toIndex, 0, moved);
    if (!nested[activeMainModule]) nested[activeMainModule] = {};
    nested[activeMainModule][activeSubModule] = reorderedActiveSub;

    const items = renumberAllFaqs(mainOrder, nested);
    await persistFaqOrder(items, "Could not save the new order.");
  };

  const clearSubDrag = () => {
    setDragSubName(null);
    setDropTargetSubName(null);
  };

  const clearMainDrag = () => {
    setDragMainName(null);
    setDropTargetMainName(null);
  };

  const handleSubDragStart = (event, subName) => {
    if (!canDrag) {
      event.preventDefault();
      return;
    }
    event.stopPropagation();
    clearMainDrag();
    setDragSubName(subName);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", subName);
  };

  const handleSubDragOver = (event, subName) => {
    if (!canDrag || !dragSubName || dragSubName === subName) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
    setDropTargetSubName(subName);
  };

  /** Drop sub A onto sub B under the same main — reorder subs, renumber globally. */
  const handleSubDrop = async (event, mainName, targetSub) => {
    event.preventDefault();
    event.stopPropagation();
    const sourceSub = dragSubName;
    clearSubDrag();
    if (!canDrag || !sourceSub || sourceSub === targetSub) return;

    const subOrder = Object.keys(faqData[mainName] || {});
    const fromIndex = subOrder.indexOf(sourceSub);
    const toIndex = subOrder.indexOf(targetSub);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

    const reorderedSubOrder = [...subOrder];
    const [moved] = reorderedSubOrder.splice(fromIndex, 1);
    reorderedSubOrder.splice(toIndex, 0, moved);

    const mainOrder = Object.keys(faqData);
    const nested = cloneFaqNested(faqData);
    const reorderedSubs = {};
    reorderedSubOrder.forEach((sub) => {
      reorderedSubs[sub] = nested[mainName]?.[sub] || [];
    });
    nested[mainName] = reorderedSubs;

    const items = renumberAllFaqs(mainOrder, nested);
    await persistFaqOrder(items, "Could not save the new sub-module order.");
  };

  const handleMainDragStart = (event, mainName) => {
    if (!canDrag) {
      event.preventDefault();
      return;
    }
    setDragMainName(mainName);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", mainName);
  };

  const handleMainDragOver = (event, mainName) => {
    if (!canDrag || !dragMainName || dragMainName === mainName) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDropTargetMainName(mainName);
  };

  /** Drop main A onto main B — reorder mains, renumber all FAQs globally. */
  const handleMainDrop = async (event, targetMain) => {
    event.preventDefault();
    event.stopPropagation();
    const sourceMain = dragMainName;
    clearMainDrag();
    if (!canDrag || !sourceMain || sourceMain === targetMain) return;

    const mainOrder = Object.keys(faqData);
    const fromIndex = mainOrder.indexOf(sourceMain);
    const toIndex = mainOrder.indexOf(targetMain);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

    const reorderedMainOrder = [...mainOrder];
    const [moved] = reorderedMainOrder.splice(fromIndex, 1);
    reorderedMainOrder.splice(toIndex, 0, moved);

    const nested = cloneFaqNested(faqData);
    const items = renumberAllFaqs(reorderedMainOrder, nested);
    await persistFaqOrder(items, "Could not save the new module order.");
  };

  const formMainOptions = useMemo(() => {
    const set = new Set(mainModules);
    if (form.mainModule && !creatingMainModule) set.add(form.mainModule);
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [mainModules, form.mainModule, creatingMainModule]);

  const formSubOptions = useMemo(() => {
    const set = new Set(
      rows
        .filter((r) => !form.mainModule || r.mainModule === form.mainModule)
        .map((r) => r.subModule)
        .filter(Boolean),
    );
    if (form.subModule && !creatingSubModule) set.add(form.subModule);
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [rows, form.mainModule, form.subModule, creatingSubModule]);

  const selectedMainOption = useMemo(() => {
    if (creatingMainModule || !form.mainModule) return null;
    return (
      formMainOptions.find((opt) => opt.value === form.mainModule) || {
        value: form.mainModule,
        label: form.mainModule,
      }
    );
  }, [creatingMainModule, form.mainModule, formMainOptions]);

  const selectedSubOption = useMemo(() => {
    if (creatingSubModule || !form.subModule) return null;
    return (
      formSubOptions.find((opt) => opt.value === form.subModule) || {
        value: form.subModule,
        label: form.subModule,
      }
    );
  }, [creatingSubModule, form.subModule, formSubOptions]);

  const startEdit = (row) => {
    setForm({
      faqId: row.faqId || "",
      mainModule: row.mainModule || "",
      subModule: row.subModule || "",
      question: row.question || "",
      answer: row.answer || "",
      videoLink: row.videoLink || "",
    });
    setCreatingMainModule(false);
    setCreatingSubModule(false);
    setFormOpen(true);
  };

  const openNewFaq = () => {
    setForm({
      ...EMPTY_FORM,
      mainModule: activeMainModule || "",
      subModule: activeSubModule || "",
    });
    setCreatingMainModule(false);
    setCreatingSubModule(false);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setForm(EMPTY_FORM);
    setCreatingMainModule(false);
    setCreatingSubModule(false);
  };

  const handleMainModuleSelect = (option) => {
    if (!option) {
      setForm((f) => ({ ...f, mainModule: "", subModule: "" }));
      setCreatingMainModule(false);
      setCreatingSubModule(false);
      return;
    }
    if (option.value === CREATE_MAIN_MODULE_VALUE) {
      setCreatingMainModule(true);
      setCreatingSubModule(false);
      setForm((f) => ({ ...f, mainModule: "", subModule: "" }));
      return;
    }
    setCreatingMainModule(false);
    setCreatingSubModule(false);
    setForm((f) => ({
      ...f,
      mainModule: option.value || "",
      subModule: "",
    }));
  };

  const handleSubModuleSelect = (option) => {
    if (!option) {
      setForm((f) => ({ ...f, subModule: "" }));
      setCreatingSubModule(false);
      return;
    }
    if (option.value === CREATE_SUB_MODULE_VALUE) {
      setCreatingSubModule(true);
      setForm((f) => ({ ...f, subModule: "" }));
      return;
    }
    setCreatingSubModule(false);
    setForm((f) => ({ ...f, subModule: option.value || "" }));
  };

  const handleSaveFaq = async (e) => {
    e.preventDefault();
    if (!staffId) {
      showAlert({
        title: "Error",
        message: "Super Admin StaffId not found in session.",
        type: "danger",
      });
      return;
    }
    if (
      !form.mainModule.trim() ||
      !form.subModule.trim() ||
      !form.question.trim() ||
      isAnswerEmpty(form.answer)
    ) {
      showAlert({
        title: "Validation",
        message: "Main module, sub module, question and answer are required.",
        type: "warning",
      });
      return;
    }

    const lengthCheck = validateAnswerLength(form.answer);
    if (!lengthCheck.allowed) {
      showAlert({
        title: "Validation",
        message: lengthCheck.message,
        type: "warning",
      });
      return;
    }

    const savedMain = form.mainModule.trim();
    const savedSub = form.subModule.trim();

    setSaving(true);
    try {
      const result = await savePlatformFaq({
        ...form,
        staffId,
        actionType: form.faqId ? "Update" : "Add",
      });
      showAlert({
        title: "Saved",
        message: result.message || "FAQ saved on current database.",
        type: "success",
      });
      closeForm();
      await loadFaqs();
      setActiveMainModule(savedMain);
      setActiveSubModule(savedSub);
      setExpandedDropdown(savedMain);
      onFaqsChanged?.();
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Could not save FAQ.",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteFaq = (faqId) => {
    setConfirmDelete({ open: true, kind: "faq", id: faqId });
  };

  const confirmDeleteAllFaqs = () => {
    setConfirmDelete({ open: true, kind: "faqAll", id: "ALL" });
  };

  const downloadExcelBuffer = async (workbook, filename) => {
    const buf = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const styleFaqExcelSheet = (ws) => {
    ws.getRow(1).font = { bold: true };
    FAQ_EXCEL_HEADERS.forEach((header, idx) => {
      if (header === "Question" || header === "Answer") {
        ws.getColumn(idx + 1).width = 40;
      } else if (header === "SortOrder") {
        ws.getColumn(idx + 1).width = 12;
      } else {
        ws.getColumn(idx + 1).width = 18;
      }
    });
  };

  const downloadFaqExcelTemplate = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("FAQs");
    ws.addRow(FAQ_EXCEL_HEADERS);
    ws.addRow([
      1,
      "CAPA",
      "CAPA FAQs",
      "Sample question one?",
      "Sample answer text",
      "",
    ]);
    ws.addRow([
      2,
      "CAPA",
      "CAPA FAQs",
      "Sample question two?",
      "Another answer",
      "",
    ]);
    styleFaqExcelSheet(ws);
    await downloadExcelBuffer(wb, "Platform_FAQ_Import_Template.xlsx");
  };

  /**
   * Export FAQs to Excel.
   * withHtml=false → Answer as plain text (readable review)
   * withHtml=true  → Answer keeps HTML (for import on another server)
   */
  const handleReportExportExcel = async ({
    rows: tableRows,
    withHtml = false,
  } = {}) => {
    const dataRows = [...(tableRows || rows || [])].sort(
      (a, b) =>
        String(a.mainModule || "").localeCompare(String(b.mainModule || "")) ||
        String(a.subModule || "").localeCompare(String(b.subModule || "")) ||
        (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0),
    );
    if (!dataRows.length) {
      showAlert({
        title: "Export",
        message: "No FAQ rows to export.",
        type: "info",
      });
      return;
    }
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("FAQs");
    ws.addRow(FAQ_EXCEL_HEADERS);
    dataRows.forEach((row) => {
      const answerValue = withHtml
        ? String(row.answer || "")
        : toPlainText(row.answer) || "";
      ws.addRow([
        Number(row.sortOrder) || 0,
        row.mainModule || "",
        row.subModule || "",
        row.question || "",
        answerValue,
        row.videoLink || "",
      ]);
    });
    styleFaqExcelSheet(ws);
    const stamp = new Date().toISOString().slice(0, 10);
    const suffix = withHtml ? "HTML" : "Plain";
    await downloadExcelBuffer(
      wb,
      `Platform_FAQs_Export_${suffix}_${stamp}.xlsx`,
    );
  };

  const openExcelUploadModal = () => {
    setExcelFile(null);
    setExcelPreviewRows([]);
    setExcelParsing(false);
    setExcelUploadOpen(true);
  };

  const closeExcelUploadModal = () => {
    setExcelUploadOpen(false);
    setExcelFile(null);
    setExcelPreviewRows([]);
    setExcelParsing(false);
    if (excelInputRef.current) excelInputRef.current.value = "";
  };

  const parseFaqExcelFile = async (file) => {
    const wb = new ExcelJS.Workbook();
    const buffer = await file.arrayBuffer();
    await wb.xlsx.load(buffer);
    const ws = wb.worksheets[0];
    if (!ws) throw new Error("Excel file has no worksheet.");

    const headerRow = ws.getRow(1);
    const headerMap = {};
    headerRow.eachCell((cell, col) => {
      const raw = String(cell.text || cell.value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "");
      if (!raw) return;
      if (["sortorder", "sort", "sortnumber", "order"].includes(raw))
        headerMap.sortOrder = col;
      else if (["mainmodule", "main"].includes(raw)) headerMap.mainModule = col;
      else if (["submodule", "sub"].includes(raw)) headerMap.subModule = col;
      else if (["question"].includes(raw)) headerMap.question = col;
      else if (["answer"].includes(raw)) headerMap.answer = col;
      else if (["videolink", "video", "videourl"].includes(raw))
        headerMap.videoLink = col;
    });

    if (
      !headerMap.mainModule ||
      !headerMap.subModule ||
      !headerMap.question ||
      !headerMap.answer
    ) {
      throw new Error(
        "Excel must have columns: SortOrder, MainModule, SubModule, Question, Answer (VideoLink optional).",
      );
    }

    const cellText = (row, col) => {
      if (!col) return "";
      const cell = row.getCell(col);
      const value = cell?.text ?? cell?.value;
      if (value == null) return "";
      if (typeof value === "object" && value.text) return String(value.text).trim();
      if (typeof value === "object" && value.richText) {
        return value.richText.map((p) => p.text || "").join("").trim();
      }
      return String(value).trim();
    };

    const items = [];
    ws.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      const mainModule = cellText(row, headerMap.mainModule);
      const subModule = cellText(row, headerMap.subModule);
      const question = cellText(row, headerMap.question);
      const answer = cellText(row, headerMap.answer);
      const videoLink = cellText(row, headerMap.videoLink);
      const sortRaw = cellText(row, headerMap.sortOrder);
      const sortOrder = Number.parseInt(sortRaw, 10);
      if (!mainModule && !subModule && !question && !answer) return;
      items.push({
        mainModule,
        subModule,
        question,
        answer,
        videoLink,
        sortOrder: Number.isFinite(sortOrder) && sortOrder > 0 ? sortOrder : 0,
      });
    });

    if (!items.length) throw new Error("No FAQ rows found in the Excel file.");
    return items;
  };

  const handleExcelFilePicked = async (event) => {
    const file = event.target.files?.[0] || null;
    setExcelFile(file);
    setExcelPreviewRows([]);
    if (!file) return;

    setExcelParsing(true);
    try {
      const items = await parseFaqExcelFile(file);
      setExcelPreviewRows(
        items.map((item, index) => ({
          key: `excel-row-${index}-${Date.now()}`,
          sortOrder: item.sortOrder || index + 1,
          mainModule: item.mainModule || "",
          subModule: item.subModule || "",
          question: item.question || "",
          answer: item.answer || "",
          videoLink: item.videoLink || "",
        })),
      );
    } catch (err) {
      setExcelFile(null);
      if (excelInputRef.current) excelInputRef.current.value = "";
      showAlert({
        title: "Preview failed",
        message: err?.message || "Could not read Excel file.",
        type: "danger",
      });
    } finally {
      setExcelParsing(false);
    }
  };

  const updateExcelPreviewRow = (key, field, value) => {
    setExcelPreviewRows((prev) =>
      prev.map((row) =>
        row.key === key
          ? {
              ...row,
              [field]:
                field === "sortOrder"
                  ? Number.parseInt(value, 10) || 0
                  : value,
            }
          : row,
      ),
    );
  };

  const removeExcelPreviewRow = (key) => {
    setExcelPreviewRows((prev) => prev.filter((row) => row.key !== key));
  };

  const handleExcelImport = async () => {
    if (!staffId) return;
    const items = excelPreviewRows
      .map((row) => ({
        sortOrder: Number(row.sortOrder) > 0 ? Number(row.sortOrder) : 0,
        mainModule: String(row.mainModule || "").trim(),
        subModule: String(row.subModule || "").trim(),
        question: String(row.question || "").trim(),
        answer: String(row.answer || "").trim(),
        videoLink: String(row.videoLink || "").trim(),
      }))
      .filter(
        (row) =>
          row.mainModule || row.subModule || row.question || row.answer,
      );

    if (!items.length) {
      showAlert({
        title: "Validation",
        message: "Load an Excel file and keep at least one preview row.",
        type: "warning",
      });
      return;
    }

    const invalid = items.find(
      (row) =>
        !row.mainModule || !row.subModule || !row.question || !row.answer,
    );
    if (invalid) {
      showAlert({
        title: "Validation",
        message:
          "Every preview row needs MainModule, SubModule, Question and Answer.",
        type: "warning",
      });
      return;
    }

    setSaving(true);
    try {
      const result = await importPlatformFaqs({ staffId, items });
      await loadFaqs();
      onFaqsChanged?.();
      const failCount = (result.results || []).filter((r) => !r.success).length;
      closeExcelUploadModal();
      showAlert({
        title: "Excel import",
        message: result.message || `Imported ${items.length} row(s).`,
        type: failCount ? "warning" : "success",
      });
    } catch (err) {
      showAlert({
        title: "Import failed",
        message: err?.message || "Could not import Excel FAQs.",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const clearGuidePreview = useCallback(() => {
    const prev = guidePreviewUrlRef.current;
    if (prev && String(prev).startsWith("blob:")) {
      URL.revokeObjectURL(prev);
    }
    guidePreviewUrlRef.current = null;
    setGuidePreview(null);
    setGuidePreviewLoading(false);
  }, []);

  const setGuidePreviewFromUrl = useCallback(
    ({ url, title, fileName, source, moduleKey }) => {
      const prev = guidePreviewUrlRef.current;
      if (prev && prev !== url && String(prev).startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      guidePreviewUrlRef.current = url;
      setGuidePreview({
        url,
        title: title || "User guide",
        fileName: fileName || "guide.pdf",
        source: source || "existing",
        moduleKey: moduleKey || "",
      });
      setGuidePreviewLoading(false);
    },
    [],
  );

  const resetGuideModuleSelection = useCallback(() => {
    setCreatingGuideModule(false);
    setGuideModuleKey((k) => k + 1);
  }, []);

  const openGuidesModal = () => {
    setGuideForm(EMPTY_GUIDE_FORM);
    setGuideConfirm(null);
    resetGuideModuleSelection();
    clearGuidePreview();
    setGuidesOpen(true);
    loadGuides();
  };

  const closeGuidesModal = () => {
    setGuidesOpen(false);
    setGuideForm(EMPTY_GUIDE_FORM);
    setGuideConfirm(null);
    resetGuideModuleSelection();
    clearGuidePreview();
  };

  const findExistingGuide = (moduleName) =>
    findGuideByModule(guides, moduleName);

  const performGuideSave = async ({ moduleName, moduleKey, documentUrl }) => {
    if (!staffId) {
      showAlert({
        title: "Error",
        message: "Super Admin StaffId not found in session.",
        type: "danger",
      });
      return;
    }
    const url = String(documentUrl || "").trim();
    if (!isValidGuideUrl(url)) {
      showAlert({
        title: "Validation",
        message: "Enter a valid http or https link to an HTML or PDF document.",
        type: "warning",
      });
      return;
    }
    setGuideSaving(true);
    try {
      const result = await savePlatformUserGuide({
        staffId,
        moduleName: moduleKey || moduleName,
        fileUrl: url,
        fileName: moduleName,
      });
      showAlert({
        title: "Saved",
        message: result.message || "User guide saved.",
        type: "success",
      });
      setGuideForm(EMPTY_GUIDE_FORM);
      resetGuideModuleSelection();
      clearGuidePreview();
      await loadGuides();
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Could not save user guide.",
        type: "danger",
      });
    } finally {
      setGuideSaving(false);
      setGuideConfirm(null);
    }
  };

  const handleGuideModuleSelect = (option) => {
    if (!option) return;
    if (option.value === CREATE_GUIDE_MODULE_VALUE) {
      setCreatingGuideModule(true);
      setGuideForm((f) => ({ ...f, moduleName: "" }));
      return;
    }
    setCreatingGuideModule(false);
    const moduleName = option.value || "";
    const existing = findGuideByModule(guides, moduleName);
    setGuideForm((f) => ({
      ...f,
      moduleName,
      documentUrl: existing?.fileUrl || f.documentUrl || "",
    }));
    if (existing?.fileUrl) {
      previewExistingGuide(existing);
    }
  };

  const previewDraftGuideUrl = (url, moduleName) => {
    const trimmed = String(url || "").trim();
    if (!isValidGuideUrl(trimmed)) {
      if (guidePreview?.source === "draft") clearGuidePreview();
      return;
    }
    setGuidePreviewFromUrl({
      url: trimmed,
      title: moduleName || "New guide",
      fileName: inferGuideKind(trimmed) === "pdf" ? "guide.pdf" : "guide.html",
      source: "draft",
      moduleKey: "",
    });
  };

  const handleSaveGuide = async (e) => {
    e.preventDefault();
    const moduleName = guideForm.moduleName.trim();
    const documentUrl = guideForm.documentUrl.trim();
    if (!moduleName || !isValidGuideUrl(documentUrl)) {
      showAlert({
        title: "Validation",
        message: "Select a module and enter a valid HTML or PDF document link.",
        type: "warning",
      });
      return;
    }
    const existing = findExistingGuide(moduleName);
    if (existing) {
      setGuideConfirm({
        kind: "replace",
        moduleName,
        moduleKey: existing.moduleKey,
        documentUrl,
      });
      return;
    }
    await performGuideSave({ moduleName, documentUrl });
  };

  const previewExistingGuide = async (guide) => {
    const moduleKey = guide?.moduleKey || guide?.moduleName;
    if (!moduleKey || guidePreviewLoading) return;
    // Pending upload takes over preview — drop it when user opens an existing guide.
    setCreatingGuideModule(false);
    setGuideForm((f) => ({
      ...f,
      moduleName: guide.moduleName || guide.moduleKey || "",
      documentUrl: guide.fileUrl || "",
    }));
    clearGuidePreview();
    setGuidePreviewLoading(true);
    try {
      let fileUrl = String(guide?.fileUrl || "").trim();
      let title = guide.moduleName || moduleKey;
      let fileName = guide.fileName || "Guide";

      if (!fileUrl) {
        const file = await fetchPlatformUserGuideFile(moduleKey);
        fileUrl = String(file?.fileUrl || "").trim();
        title = file.moduleName || title;
        fileName = file.fileName || fileName;
      }

      if (!fileUrl) {
        throw new Error("This user guide has no document link to display.");
      }

      setGuideForm((f) => ({
        ...f,
        moduleName: title,
        documentUrl: fileUrl,
      }));
      setGuidePreviewFromUrl({
        url: fileUrl,
        title,
        fileName,
        source: "existing",
        moduleKey,
      });
    } catch (err) {
      clearGuidePreview();
      showAlert({
        title: "Preview",
        message: err?.message || "Could not load the user guide.",
        type: "danger",
      });
    }
  };

  const canDragGuides =
    !guideSaving && !guidesReordering && !guidesLoading && guides.length > 1;

  const handleGuideCardDragStart = (event, moduleKey) => {
    if (!canDragGuides) {
      event.preventDefault();
      return;
    }
    setDragGuideKey(moduleKey);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", moduleKey);
  };

  const handleGuideCardDragOver = (event, moduleKey) => {
    if (!canDragGuides || !dragGuideKey || dragGuideKey === moduleKey) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDropTargetGuideKey(moduleKey);
  };

  const handleGuideCardDrop = async (event, targetKey) => {
    event.preventDefault();
    const sourceKey = dragGuideKey;
    setDragGuideKey(null);
    setDropTargetGuideKey(null);
    if (!canDragGuides || !sourceKey || sourceKey === targetKey) return;

    const order = guides.map((g) => g.moduleKey);
    const fromIndex = order.indexOf(sourceKey);
    const toIndex = order.indexOf(targetKey);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

    const nextOrder = [...order];
    const [moved] = nextOrder.splice(fromIndex, 1);
    nextOrder.splice(toIndex, 0, moved);

    const reordered = nextOrder
      .map((key, index) => {
        const guide = guides.find((g) => g.moduleKey === key);
        return guide
          ? { ...guide, sortOrder: index + 1 }
          : null;
      })
      .filter(Boolean);

    setGuides(reordered);
    setGuidesReordering(true);
    try {
      await reorderPlatformUserGuides({
        staffId,
        items: reordered.map((g) => ({
          moduleKey: g.moduleKey,
          sortOrder: g.sortOrder,
        })),
      });
    } catch (err) {
      showAlert({
        title: "Reorder failed",
        message: err?.message || "Could not save user guide order.",
        type: "danger",
      });
      await loadGuides();
    } finally {
      setGuidesReordering(false);
    }
  };

  const handleReplaceGuideClick = (guide, event) => {
    event?.stopPropagation?.();
    setCreatingGuideModule(false);
    setGuideForm({
      moduleName: guide.moduleName || guide.moduleKey || "",
      documentUrl: guide.fileUrl || "",
    });
    previewExistingGuide(guide);
  };

  const handleDeleteGuideClick = (guide, event) => {
    event?.stopPropagation?.();
    setGuideConfirm({
      kind: "delete",
      moduleName: guide.moduleName || guide.moduleKey,
      moduleKey: guide.moduleKey,
    });
  };

  const runGuideConfirm = async () => {
    if (!guideConfirm) return;
    if (guideConfirm.kind === "replace") {
      await performGuideSave({
        moduleName: guideConfirm.moduleName,
        moduleKey: guideConfirm.moduleKey,
        documentUrl: guideConfirm.documentUrl,
      });
      return;
    }
    if (guideConfirm.kind === "delete") {
      if (!staffId) {
        setGuideConfirm(null);
        return;
      }
      setGuideSaving(true);
      try {
        const deletedKey =
          guideConfirm.moduleKey || guideConfirm.moduleName || "";
        const result = await deletePlatformUserGuide({
          staffId,
          moduleName: deletedKey,
        });
        showAlert({
          title: "Deleted",
          message: result.message || "User guide deleted.",
          type: "success",
        });
        if (
          guidePreview?.moduleKey &&
          guidePreview.moduleKey.toLowerCase() === deletedKey.toLowerCase()
        ) {
          clearGuidePreview();
        }
        await loadGuides();
      } catch (err) {
        showAlert({
          title: "Error",
          message: err?.message || "Could not delete user guide.",
          type: "danger",
        });
      } finally {
        setGuideSaving(false);
        setGuideConfirm(null);
      }
    }
  };

  const requestHtmlExport = () => {
    if (!rows.length) {
      showAlert({
        title: "Export",
        message: "No FAQ rows to export.",
        type: "info",
      });
      return;
    }
    setConfirmDelete({ open: true, kind: "export-html", id: "HTML" });
  };

  const runConfirmedDelete = async () => {
    const { kind, id } = confirmDelete;
    setConfirmDelete({ open: false, kind: null, id: null });
    if (!kind) return;

    if (kind === "export-html") {
      await handleReportExportExcel({ rows, withHtml: true });
      return;
    }

    if (!staffId) return;
    if (kind !== "faqAll" && !id) return;

    setSaving(true);
    try {
      if (kind === "faq") {
        await deletePlatformFaq({ faqId: id, staffId });
        await loadFaqs();
        onFaqsChanged?.();
        showAlert({
          title: "Deleted",
          message: "FAQ permanently deleted from the current database.",
          type: "success",
        });
      } else if (kind === "faqAll") {
        const result = await deleteAllPlatformFaqs({ staffId });
        await loadFaqs();
        onFaqsChanged?.();
        showAlert({
          title: "Deleted",
          message: result.message || "All FAQs permanently deleted.",
          type: "success",
        });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Delete failed.",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const answerCharCount = toPlainText(form.answer).length;

  const breadcrumbActions = [
    {
      icon: "fas fa-refresh",
      label: "Refresh",
      onClick: loadFaqs,
      disabled: loading || saving || reordering,
    },
    {
      icon: "fas fa-plus",
      label: "New FAQ",
      onClick: openNewFaq,
      disabled: saving,
    },
    {
      icon: "fas fa-book",
      label: "User Guides",
      onClick: openGuidesModal,
      disabled: saving || !staffId,
    },
    {
      icon: "fas fa-file-upload",
      label: "Upload Excel",
      onClick: openExcelUploadModal,
      disabled: saving || !staffId,
    },
    {
      icon: "fas fa-file-excel",
      label: "Export plain (Text Only)",
      onClick: () => handleReportExportExcel({ rows, withHtml: false }),
      disabled: saving || !rows.length,
    },
    {
      icon: "fas fa-file-code",
      label: "Export HTML (Formatted)",
      onClick: requestHtmlExport,
      disabled: saving || !rows.length,
    },
    {
      icon: "fas fa-trash",
      label: "Delete all FAQs",
      onClick: confirmDeleteAllFaqs,
      disabled: saving || !rows.length,
    },
  ];

  if (!open) return null;

  const shellClass = asPage
    ? "faq-manage-modal faq-manage-modal--page"
    : "faq-manage-modal";

  return (
    <>
      {asPage ? (
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            {
              label: (
                <span className="faq-manage-breadcrumb-current">
                  <span className="faq-manage-breadcrumb-title">
                    {categoryTitle}
                  </span>
                  <span className="faq-manage-chip">Manage</span>
                </span>
              ),
            },
          ]}
          actions={breadcrumbActions}
          trailingExtras={
            <span className="faq-questions-count faq-manage-breadcrumb-count">
              Showing {filteredFAQs.length} results
            </span>
          }
        />
      ) : null}

      {asPage && pageBanner ? pageBanner : null}

      <div className={shellClass} onClick={asPage ? undefined : onClose}>
        <div
          className="faq-manage-modal__content"
          onClick={asPage ? undefined : (e) => e.stopPropagation()}
          role={asPage ? "region" : "dialog"}
          aria-modal={asPage ? undefined : true}
          aria-label="Manage FAQs"
        >
          {!asPage ? (
            <header className="faq-manage-modal__header">
              <div>
                <h2 id="faq-manage-title">Manage FAQs</h2>
              </div>
              <div className="faq-manage-modal__header-actions">
                <button
                  type="button"
                  className="faq-pdf-close-btn"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            </header>
          ) : null}

          <div className="faq-manage-modal__body faq-manage-modal__body--browser">
            {loading ? <p className="text-muted p-2 mb-0">Loading…</p> : null}

            <div className="faq-manage-browser-card">
            <div className="faq-manage-browser faq-page">
              <main className="faq-main">
                <aside className="faq-sidebar">
                  <nav className="faq-nav">
                    {categories.map((category) => {
                      const isExpanded = expandedDropdown === category.id;
                      const hasSubItems = category.subItems?.length > 0;
                      const isMainDragging = dragMainName === category.id;
                      const isMainDropTarget =
                        dropTargetMainName === category.id;
                      return (
                        <div
                          key={category.id}
                          className={`faq-manage-main-wrap${isMainDragging ? " is-dragging" : ""}${isMainDropTarget ? " is-drop-target" : ""}`}
                          draggable={canDrag}
                          onDragStart={(e) =>
                            handleMainDragStart(e, category.id)
                          }
                          onDragOver={(e) =>
                            handleMainDragOver(e, category.id)
                          }
                          onDragLeave={() => {
                            if (dropTargetMainName === category.id)
                              setDropTargetMainName(null);
                          }}
                          onDrop={(e) => handleMainDrop(e, category.id)}
                          onDragEnd={clearMainDrag}
                        >
                          {hasSubItems ? (
                            <div className="faq-nav-group">
                              <button
                                type="button"
                                className={`faq-nav-button ${activeMainModule === category.id ? "active" : ""}`}
                                onClick={() => {
                                  setExpandedDropdown(
                                    isExpanded ? "" : category.id,
                                  );
                                  if (!isExpanded) {
                                    const subModules = Object.keys(
                                      faqData[category.id] || {},
                                    );
                                    setActiveMainModule(category.id);
                                    setActiveSubModule(subModules[0] || "");
                                  }
                                }}
                              >
                                <div className="faq-nav-button-content">
                                  {canDrag ? (
                                    <span
                                      className="faq-manage-subdrag-handle"
                                      title="Drag to reorder module"
                                      aria-hidden="true"
                                    >
                                      <i
                                        className="fas fa-grip-vertical"
                                        aria-hidden="true"
                                      />
                                    </span>
                                  ) : (
                                    <span className="faq-nav-dot"></span>
                                  )}
                                  <span className="faq-nav-label">
                                    {category.label}
                                  </span>
                                </div>
                                <i
                                  className={`fas fa-chevron-right faq-nav-chevron ${isExpanded ? "expanded" : ""}`}
                                ></i>
                              </button>
                              {isExpanded && (
                                <div className="faq-nav-submenu">
                                  {category.subItems.map((subItem) => {
                                    const isSubDragging =
                                      dragSubName === subItem;
                                    const isSubDropTarget =
                                      dropTargetSubName === subItem;
                                    return (
                                      <a
                                        key={subItem}
                                        href="#"
                                        className={`faq-nav-subitem ${activeSubModule === subItem ? "active" : ""}${isSubDragging ? " is-dragging" : ""}${isSubDropTarget ? " is-drop-target" : ""}`}
                                        draggable={canDrag}
                                        onDragStart={(e) =>
                                          handleSubDragStart(e, subItem)
                                        }
                                        onDragOver={(e) =>
                                          handleSubDragOver(e, subItem)
                                        }
                                        onDragLeave={() => {
                                          if (dropTargetSubName === subItem)
                                            setDropTargetSubName(null);
                                        }}
                                        onDrop={(e) =>
                                          handleSubDrop(
                                            e,
                                            category.id,
                                            subItem,
                                          )
                                        }
                                        onDragEnd={clearSubDrag}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setActiveMainModule(category.id);
                                          setActiveSubModule(subItem);
                                        }}
                                      >
                                        {canDrag && (
                                          <span
                                            className="faq-manage-subdrag-handle"
                                            title="Drag to reorder"
                                            aria-hidden="true"
                                          >
                                            <i
                                              className="fas fa-grip-vertical"
                                              aria-hidden="true"
                                            />
                                          </span>
                                        )}
                                        <span className="faq-nav-subitem-label">
                                          {subItem}
                                        </span>
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              className={`faq-nav-button ${activeMainModule === category.id ? "active" : ""}`}
                              onClick={() => {
                                setActiveMainModule(category.id);
                                setActiveSubModule("");
                              }}
                            >
                              <div className="faq-nav-button-content">
                                {canDrag ? (
                                  <span
                                    className="faq-manage-subdrag-handle"
                                    title="Drag to reorder module"
                                    aria-hidden="true"
                                  >
                                    <i
                                      className="fas fa-grip-vertical"
                                      aria-hidden="true"
                                    />
                                  </span>
                                ) : null}
                                <span className="faq-nav-label">
                                  {category.label}
                                </span>
                              </div>
                              <i className="fas fa-chevron-right"></i>
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {categories.length === 0 ? (
                      <p className="text-muted small px-2">
                        No FAQs yet — use "New FAQ" to add the first one.
                      </p>
                    ) : null}
                  </nav>
                </aside>

                <section className="faq-content">
                  <div className="faq-questions-section">
                    <div className="faq-manage-list-head">
                      <div>
                        <h2 className="faq-manage-list-title">{categoryTitle}</h2>
                        <p className="faq-manage-list-sub">
                          Drag to reorder · Expand a row to preview the answer ·
                          Use Edit to change content
                        </p>
                      </div>
                      <span className="faq-manage-list-count">
                        {filteredFAQs.length}{" "}
                        {filteredFAQs.length === 1 ? "question" : "questions"}
                      </span>
                    </div>
                    <div className="faq-search-section faq-search-section--manage">
                      <div className="faq-search-wrapper">
                        <i className="fas fa-search faq-search-icon"></i>
                        <input
                          type="text"
                          className="faq-search-input"
                          placeholder="Search for questions, keywords, or topics..."
                          value={searchQuery}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSearchQuery(value);
                            setShowSuggestions(value.length > 0);
                          }}
                          onFocus={() =>
                            setShowSuggestions(searchQuery.length > 0)
                          }
                          onBlur={() =>
                            setTimeout(
                              () => setShowSuggestions(false),
                              SUGGESTION_DELAY,
                            )
                          }
                        />
                        {showSuggestions && filteredKeywords.length > 0 && (
                          <div className="faq-keyword-suggestions">
                            <div className="faq-keyword-label">Keywords:</div>
                            <div className="faq-keywords-list">
                              {filteredKeywords.map((keyword) => (
                                <button
                                  key={keyword}
                                  className="faq-keyword-tag"
                                  onClick={() => {
                                    setSearchQuery(keyword);
                                    setShowSuggestions(false);
                                  }}
                                >
                                  {keyword}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="faq-questions-list faq-questions-list--manage">
                      {filteredFAQs.map((faq) => {
                        const isExpanded = expandedItems.has(faq.faqId);
                        const isDragging = dragFaqId === faq.faqId;
                        const isDropTarget = dropTargetFaqId === faq.faqId;
                        const canDragItem = canDrag && !!faq.faqId;
                        return (
                          <div
                            key={faq.faqId || faq.question}
                            className={`faq-item faq-item--manage ${isExpanded ? "expanded" : ""}${isDragging ? " is-dragging" : ""}${isDropTarget ? " is-drop-target" : ""}`}
                            draggable={canDragItem}
                            onDragStart={(e) => handleDragStart(e, faq)}
                            onDragOver={(e) => handleDragOver(e, faq)}
                            onDragLeave={() => {
                              if (dropTargetFaqId === faq.faqId)
                                setDropTargetFaqId(null);
                            }}
                            onDrop={(e) => handleDrop(e, faq)}
                            onDragEnd={clearDrag}
                          >
                            <div className="faq-item-row">
                              {canDragItem && (
                                <span
                                  className="faq-manage-drag-handle"
                                  title="Drag to reorder"
                                >
                                  <i
                                    className="fas fa-grip-vertical"
                                    aria-hidden="true"
                                  />
                                </span>
                              )}
                              <button
                                type="button"
                                className="faq-item-header"
                                onClick={() => toggleExpand(faq.faqId)}
                                aria-expanded={isExpanded}
                              >
                                <div className="faq-item-header-content">
                                  <span className="faq-item-question">
                                    {faq.question}
                                  </span>
                                  {searchQuery.trim() &&
                                    faq.mainModule &&
                                    faq.subModule && (
                                      <span className="faq-item-badge">
                                        {faq.mainModule} · {faq.subModule}
                                      </span>
                                    )}
                                </div>
                                <span
                                  className="faq-item-icon faq-item-icon--chevron"
                                  aria-hidden="true"
                                >
                                  <i
                                    className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}
                                  />
                                </span>
                              </button>
                              <div className="faq-item-row-actions">
                                <button
                                  type="button"
                                  className="faq-manage-icon-btn faq-manage-icon-btn--edit"
                                  onClick={() => startEdit(faq)}
                                  title="Edit FAQ"
                                  aria-label="Edit FAQ"
                                >
                                  <i
                                    className="fas fa-pen"
                                    aria-hidden="true"
                                  />
                                </button>
                                <button
                                  type="button"
                                  className="faq-manage-icon-btn faq-manage-icon-btn--delete"
                                  onClick={() => confirmDeleteFaq(faq.faqId)}
                                  disabled={!faq.faqId}
                                  title="Delete FAQ"
                                  aria-label="Delete FAQ"
                                >
                                  <i
                                    className="fas fa-trash"
                                    aria-hidden="true"
                                  />
                                </button>
                              </div>
                            </div>
                            {isExpanded ? (
                              <div className="faq-item-content expanded">
                                <FaqAnswerContent
                                  faq={faq}
                                  onPlayVideo={setSelectedVideo}
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>

                    {filteredFAQs.length === 0 && (
                      <div className="faq-no-results">
                        <p>
                          {searchQuery.trim()
                            ? "No questions found matching your search."
                            : "No questions found for this section."}
                        </p>
                      </div>
                    )}
                  </div>
                </section>
              </main>
            </div>
            </div>
          </div>
        </div>
      </div>

      {selectedVideo && (
        <div
          className="faq-pdf-modal faq-video-modal"
          onClick={() => setSelectedVideo(null)}
        >
          <div
            className="faq-pdf-modal-content faq-video-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="faq-pdf-modal-header">
              <h3 className="faq-pdf-modal-title">
                {selectedVideo.title || "Guide video"}
              </h3>
              <div className="faq-pdf-modal-actions">
                <button
                  type="button"
                  className="faq-pdf-download-btn"
                  onClick={() =>
                    window.open(
                      selectedVideo.shareUrl || selectedVideo.embedUrl,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  <i className="fas fa-external-link-alt" /> Open in new tab
                </button>
                <button
                  type="button"
                  className="faq-pdf-close-btn"
                  onClick={() => setSelectedVideo(null)}
                  aria-label="Close video"
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            </div>
            <div className="faq-pdf-modal-body faq-video-modal-body">
              <div className="faq-video-embed-wrap">
                <iframe
                  className="faq-video-iframe"
                  src={selectedVideo.embedUrl}
                  title={selectedVideo.title || "Guide video"}
                  frameBorder="0"
                  referrerPolicy="unsafe-url"
                  allowFullScreen
                  allow="clipboard-write"
                  sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {formOpen ? (
        <div
          className="modal fade show d-block faq-manage-bs-modal-backdrop"
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl faq-manage-bs-modal-dialog">
            <div className="modal-content faq-manage-bs-modal">
              <div className="modal-header">
                <h5 className="modal-title" id="faq-form-title">
                  {form.faqId ? "Edit FAQ" : "Add FAQ"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeForm}
                  aria-label="Close"
                />
              </div>
              <form onSubmit={handleSaveFaq}>
                <div className="modal-body faq-manage-bs-modal__body">
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label">
                        Main module <span className="text-danger">*</span>
                      </label>
                      {creatingMainModule ? (
                        <div className="faq-manage-sub-create">
                          <input
                            className="form-control"
                            placeholder="Enter new main module"
                            value={form.mainModule}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                mainModule: e.target.value,
                                subModule: "",
                              }))
                            }
                            required
                            autoFocus
                          />
                          <button
                            type="button"
                            className="btn btn-link btn-sm px-0"
                            onClick={() => {
                              setCreatingMainModule(false);
                              setCreatingSubModule(false);
                              setForm((f) => ({
                                ...f,
                                mainModule: "",
                                subModule: "",
                              }));
                            }}
                          >
                            Choose existing
                          </button>
                        </div>
                      ) : (
                        <Dropdown
                          options={[
                            ...formMainOptions,
                            CREATE_MAIN_MODULE_OPTION,
                          ]}
                          selected={selectedMainOption}
                          onSelect={handleMainModuleSelect}
                          showSearch
                        />
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Sub module <span className="text-danger">*</span>
                      </label>
                      {creatingSubModule ? (
                        <div className="faq-manage-sub-create">
                          <input
                            className="form-control"
                            placeholder="Enter new sub module"
                            value={form.subModule}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                subModule: e.target.value,
                              }))
                            }
                            required
                            autoFocus
                          />
                          <button
                            type="button"
                            className="btn btn-link btn-sm px-0"
                            onClick={() => {
                              setCreatingSubModule(false);
                              setForm((f) => ({ ...f, subModule: "" }));
                            }}
                          >
                            Choose existing
                          </button>
                        </div>
                      ) : (
                        <Dropdown
                          options={[
                            ...formSubOptions,
                            CREATE_SUB_MODULE_OPTION,
                          ]}
                          selected={selectedSubOption}
                          onSelect={handleSubModuleSelect}
                          showSearch
                          disabled={!form.mainModule}
                        />
                      )}
                    </div>
                    <div className="col-12">
                      <label className="form-label">
                        Question <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={form.question}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, question: e.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-baseline mb-1">
                        <label className="form-label mb-0">
                          Answer <span className="text-danger">*</span>
                        </label>
                        <span
                          className={
                            answerCharCount > ANSWER_MAX_LENGTH
                              ? "faq-manage-form-modal__count is-over"
                              : "faq-manage-form-modal__count"
                          }
                        >
                          {answerCharCount.toLocaleString()} /{" "}
                          {ANSWER_MAX_LENGTH.toLocaleString()}
                        </span>
                      </div>
                      <TextEditor
                        value={form.answer}
                        onChange={(html) =>
                          setForm((f) => ({ ...f, answer: html }))
                        }
                        placeholder="Write the answer…"
                        height="9rem"
                        minHeight="8rem"
                        toolbarVariant="compact"
                        showImageButton={false}
                        showCodeBlock={false}
                        sanitizeHtml
                        blockImages
                        validateContentBeforeApply={validateAnswerLength}
                        onContentBlocked={(message) =>
                          showAlert({
                            title: "Answer limit",
                            message:
                              message ||
                              `Answer cannot exceed ${ANSWER_MAX_LENGTH.toLocaleString()} characters.`,
                            type: "warning",
                          })
                        }
                        className="faq-manage-form-modal__editor"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Video link (optional)</label>
                      <input
                        className="form-control"
                        type="text"
                        inputMode="url"
                        placeholder="https://app.supademo.com/demo/… or demo id"
                        value={form.videoLink}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, videoLink: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeForm}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving || answerCharCount > ANSWER_MAX_LENGTH}
                  >
                    {saving ? "Saving…" : "Save on current DB"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {excelUploadOpen ? (
        <div
          className="modal fade show d-block faq-manage-bs-modal-backdrop faq-manage-excel-modal-overlay"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog faq-manage-excel-modal-dialog">
            <div className="modal-content faq-manage-bs-modal faq-manage-excel-modal">
              <div className="modal-header py-2">
                <h5 className="modal-title mb-0">Import FAQs</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeExcelUploadModal}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body faq-manage-excel-modal__body">
                <div className="faq-import-sheet">
                  <div className="faq-import-sheet__toolbar">
                    <div className="faq-import-sheet__toolbar-left">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={downloadFaqExcelTemplate}
                        disabled={saving || excelParsing}
                      >
                        <i className="fas fa-download me-1" aria-hidden="true" />
                        Download template
                      </button>
                      <label
                        className={`btn btn-outline-primary btn-sm mb-0${
                          saving || excelParsing ? " disabled" : ""
                        }`}
                      >
                        <i className="fas fa-upload me-1" aria-hidden="true" />
                        {excelParsing ? "Reading…" : "Choose file"}
                        <input
                          ref={excelInputRef}
                          type="file"
                          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          className="d-none"
                          onChange={handleExcelFilePicked}
                          disabled={saving || excelParsing}
                        />
                      </label>
                      {excelFile?.name ? (
                        <span className="faq-import-sheet__filename">
                          {excelFile.name}
                        </span>
                      ) : null}
                    </div>
                    {excelPreviewRows.length > 0 ? (
                      <span className="faq-import-sheet__count">
                        {excelPreviewRows.length} FAQ(s) · edit inline before
                        import
                      </span>
                    ) : null}
                  </div>

                  {excelPreviewRows.length === 0 ? (
                    <div className="faq-import-sheet__empty">
                      {excelParsing
                        ? "Reading Excel file…"
                        : "Upload an Excel file using the template columns: SortOrder, MainModule, SubModule, Question, Answer, optional VideoLink. For another server, use “Export HTML (Formatted)” so answer formatting is preserved."}
                    </div>
                  ) : (
                    <div className="faq-import-grid-wrap">
                      <table className="faq-import-grid">
                        <thead>
                          <tr>
                            <th className="faq-import-grid__col-num">#</th>
                            <th className="faq-import-grid__col-sort">Sort</th>
                            <th className="faq-import-grid__col-module">
                              Main module
                            </th>
                            <th className="faq-import-grid__col-module">
                              Sub module
                            </th>
                            <th className="faq-import-grid__col-question">
                              Question
                            </th>
                            <th className="faq-import-grid__col-answer">
                              Answer
                            </th>
                            <th className="faq-import-grid__col-video">
                              Video link
                            </th>
                            <th
                              className="faq-import-grid__col-actions"
                              aria-label="Actions"
                            />
                          </tr>
                        </thead>
                        <tbody>
                          {excelPreviewRows.map((row, idx) => (
                            <tr key={row.key}>
                              <td className="faq-import-grid__num">
                                {idx + 1}
                              </td>
                              <td>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={row.sortOrder || ""}
                                  onChange={(e) =>
                                    updateExcelPreviewRow(
                                      row.key,
                                      "sortOrder",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.mainModule}
                                  onChange={(e) =>
                                    updateExcelPreviewRow(
                                      row.key,
                                      "mainModule",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.subModule}
                                  onChange={(e) =>
                                    updateExcelPreviewRow(
                                      row.key,
                                      "subModule",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.question}
                                  onChange={(e) =>
                                    updateExcelPreviewRow(
                                      row.key,
                                      "question",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.answer}
                                  onChange={(e) =>
                                    updateExcelPreviewRow(
                                      row.key,
                                      "answer",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.videoLink}
                                  onChange={(e) =>
                                    updateExcelPreviewRow(
                                      row.key,
                                      "videoLink",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="faq-import-grid__actions">
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm text-danger p-0"
                                  title="Remove row"
                                  onClick={() =>
                                    removeExcelPreviewRow(row.key)
                                  }
                                  disabled={saving}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer py-2">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={closeExcelUploadModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleExcelImport}
                  disabled={
                    saving || excelParsing || !excelPreviewRows.length
                  }
                >
                  {saving
                    ? "Importing…"
                    : `Import${
                        excelPreviewRows.length
                          ? ` (${excelPreviewRows.length})`
                          : ""
                      }`}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {guidesOpen ? (
        <div
          className="modal fade show d-block faq-manage-bs-modal-backdrop"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered faq-manage-bs-modal-dialog faq-manage-guides-dialog">
            <div className="modal-content faq-manage-bs-modal faq-manage-guides-modal">
              <div className="modal-header">
                <h5 className="modal-title">User Guides</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeGuidesModal}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body faq-manage-bs-modal__body">
                <form
                  className="faq-manage-guides-form"
                  onSubmit={handleSaveGuide}
                >
                  <div className="faq-manage-guides-layout">
                    <section className="faq-manage-guides-col faq-manage-guides-col--list">
                      <div className="faq-manage-guides-col__title">
                        Saved guides
                      </div>
                      <div className="faq-manage-guides-list">
                        {guidesLoading ? (
                          <p className="text-muted small mb-0 text-center">
                            Loading…
                          </p>
                        ) : guides.length === 0 ? (
                          <p className="text-muted small mb-0 text-center">
                            No user guides added yet.
                          </p>
                        ) : (
                          <ul className="faq-manage-guides-cards">
                            {guides.map((guide) => {
                              const isActive =
                                guidePreview?.source === "existing" &&
                                guidePreview?.moduleKey &&
                                guide.moduleKey.toLowerCase() ===
                                  guidePreview.moduleKey.toLowerCase();
                              const isDragging =
                                dragGuideKey === guide.moduleKey;
                              const isDropTarget =
                                dropTargetGuideKey === guide.moduleKey;
                              return (
                                <li
                                  key={guide.moduleKey}
                                  className={`faq-manage-guides-card${
                                    isActive
                                      ? " faq-manage-guides-card--active"
                                      : ""
                                  }${isDragging ? " is-dragging" : ""}${
                                    isDropTarget ? " is-drop-target" : ""
                                  }`}
                                  draggable={canDragGuides}
                                  onDragStart={(e) =>
                                    handleGuideCardDragStart(e, guide.moduleKey)
                                  }
                                  onDragOver={(e) =>
                                    handleGuideCardDragOver(e, guide.moduleKey)
                                  }
                                  onDragLeave={() => {
                                    if (dropTargetGuideKey === guide.moduleKey)
                                      setDropTargetGuideKey(null);
                                  }}
                                  onDrop={(e) =>
                                    handleGuideCardDrop(e, guide.moduleKey)
                                  }
                                  onDragEnd={() => {
                                    setDragGuideKey(null);
                                    setDropTargetGuideKey(null);
                                  }}
                                  onClick={() => previewExistingGuide(guide)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      previewExistingGuide(guide);
                                    }
                                  }}
                                  role="button"
                                  tabIndex={0}
                                >
                                  {canDragGuides ? (
                                    <span
                                      className="faq-manage-guides-card__drag"
                                      title="Drag to reorder"
                                      aria-hidden="true"
                                    >
                                      <i className="fas fa-grip-vertical" />
                                    </span>
                                  ) : null}
                                  <div className="faq-manage-guides-card__icon">
                                    <i
                                      className={
                                        inferGuideKind(guide.fileUrl) === "pdf"
                                          ? "fas fa-file-pdf"
                                          : "fas fa-globe"
                                      }
                                      aria-hidden="true"
                                    />
                                  </div>
                                  <div className="faq-manage-guides-card__meta">
                                    <div className="faq-manage-guides-card__title">
                                      {guide.moduleName || guide.moduleKey}
                                    </div>
                                    <div className="faq-manage-guides-card__sub">
                                      {formatGuideHost(guide.fileUrl)}
                                    </div>
                                  </div>
                                  <div className="faq-manage-guides-card__actions">
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm"
                                      onClick={(e) =>
                                        handleReplaceGuideClick(guide, e)
                                      }
                                      disabled={guideSaving || guidesReordering}
                                      title="Edit this guide"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={(e) =>
                                        handleDeleteGuideClick(guide, e)
                                      }
                                      disabled={guideSaving || guidesReordering}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </section>

                    <section className="faq-manage-guides-col faq-manage-guides-col--upload">
                      <div className="faq-manage-guides-col__title">
                        Add guide
                      </div>

                      <div className="faq-manage-guides-module">
                        <label className="form-label">
                          Module <span className="text-danger">*</span>
                        </label>
                        {creatingGuideModule ? (
                          <div className="faq-manage-sub-create">
                            <input
                              className="form-control"
                              placeholder="Enter new module name (e.g. CAPA)"
                              value={guideForm.moduleName}
                              onChange={(e) => {
                                const moduleName = e.target.value;
                                setGuideForm((f) => ({ ...f, moduleName }));
                                if (isValidGuideUrl(guideForm.documentUrl)) {
                                  previewDraftGuideUrl(
                                    guideForm.documentUrl,
                                    moduleName,
                                  );
                                }
                              }}
                              disabled={guideSaving}
                              required
                              autoFocus
                            />
                            <button
                              type="button"
                              className="btn btn-link btn-sm px-0"
                              onClick={() => {
                                setCreatingGuideModule(false);
                                setGuideForm((f) => ({
                                  ...f,
                                  moduleName: "",
                                }));
                              }}
                              disabled={guideSaving}
                            >
                              Choose existing
                            </button>
                          </div>
                        ) : (
                          <Dropdown
                            key={`guide-module-${guideModuleKey}`}
                            options={[
                              ...guideModuleOptions,
                              CREATE_GUIDE_MODULE_OPTION,
                            ]}
                            selected={selectedGuideModuleOption}
                            onSelect={handleGuideModuleSelect}
                            showSearch
                            disabled={guideSaving}
                          />
                        )}
                        {selectedModuleExistingGuide ? (
                          <div className="faq-manage-guides-replace-hint">
                            <i
                              className="fas fa-info-circle"
                              aria-hidden="true"
                            />
                            <span>
                              A guide already exists for{" "}
                              <strong>
                                {selectedModuleExistingGuide.moduleName ||
                                  selectedModuleExistingGuide.moduleKey}
                              </strong>
                              . Saving will replace the existing link.
                            </span>
                          </div>
                        ) : null}
                      </div>

                      <div className="faq-manage-guides-module">
                        <label className="form-label">
                          Document link <span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          type="url"
                          placeholder="https://example.com/guide.pdf"
                          value={guideForm.documentUrl}
                          onChange={(e) => {
                            const documentUrl = e.target.value;
                            setGuideForm((f) => ({ ...f, documentUrl }));
                            previewDraftGuideUrl(
                              documentUrl,
                              guideForm.moduleName,
                            );
                          }}
                          disabled={guideSaving}
                          required
                        />
                        <p className="faq-manage-guides-link-hint">
                          Paste a public HTML or PDF link. The preview updates
                          as soon as the URL is valid.
                        </p>
                      </div>

                      <div className="faq-manage-guides-actions">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={
                            guideSaving ||
                            !staffId ||
                            !guideForm.moduleName.trim() ||
                            !isValidGuideUrl(guideForm.documentUrl)
                          }
                        >
                          {guideSaving
                            ? "Saving…"
                            : selectedModuleExistingGuide
                              ? "Replace guide"
                              : "Save guide"}
                        </button>
                      </div>
                    </section>

                    <section className="faq-manage-guides-col faq-manage-guides-col--preview">
                      <div className="faq-manage-guides-col__title">
                        Preview
                        {guidePreview?.title ? (
                          <span className="faq-manage-guides-preview-name">
                            {guidePreview.title}
                          </span>
                        ) : null}
                        {guidePreview?.url ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm faq-manage-guides-preview-open"
                            onClick={() =>
                              window.open(
                                guidePreview.url,
                                "_blank",
                                "noopener,noreferrer",
                              )
                            }
                          >
                            <i className="fas fa-external-link-alt" /> Open in
                            new tab
                          </button>
                        ) : null}
                      </div>
                      <div className="faq-manage-guides-preview">
                        {guidePreviewLoading ? (
                          <div className="faq-manage-guides-preview__empty">
                            Loading preview…
                          </div>
                        ) : guidePreview?.url ? (
                          <iframe
                            key={guidePreview.url}
                            title={`Preview ${guidePreview.fileName}`}
                            src={guidePreview.url}
                            className="faq-manage-guides-preview__frame"
                          />
                        ) : (
                          <div className="faq-manage-guides-preview__empty">
                            <i className="fas fa-link" aria-hidden="true" />
                            <p>
                              Select a saved guide or paste an HTML or PDF link
                              to preview it here.
                            </p>
                          </div>
                        )}
                      </div>
                    </section>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeGuidesModal}
                  disabled={guideSaving}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        isOpen={!!guideConfirm}
        onClose={() => setGuideConfirm(null)}
        onConfirm={runGuideConfirm}
        title={
          guideConfirm?.kind === "delete"
            ? "Delete user guide?"
            : "Replace user guide?"
        }
        message={
          guideConfirm?.kind === "delete"
            ? `This permanently deletes the user guide for "${guideConfirm?.moduleName}". This cannot be undone.`
            : `A guide already exists for "${guideConfirm?.moduleName}". Replace the document link?`
        }
        confirmText={guideConfirm?.kind === "delete" ? "Delete" : "Replace"}
        confirmButtonClass={
          guideConfirm?.kind === "delete" ? "btn-danger" : "btn-primary"
        }
        icon={
          guideConfirm?.kind === "delete"
            ? "fas fa-trash text-danger"
            : "fas fa-exclamation-triangle text-warning"
        }
      />

      <ConfirmModal
        isOpen={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, kind: null, id: null })}
        onConfirm={runConfirmedDelete}
        title={
          confirmDelete.kind === "export-html"
            ? "Export HTML (Formatted)?"
            : confirmDelete.kind === "faqAll"
              ? "Delete all FAQs?"
              : "Delete FAQ?"
        }
        message={
          confirmDelete.kind === "export-html"
            ? "This file will contain many HTML tags in the Answer column. Be careful while you edit it — changing tags can break formatting when you import again."
            : confirmDelete.kind === "faqAll"
              ? "This permanently deletes ALL FAQs from the current database. This cannot be undone."
              : "This permanently deletes the FAQ from the current database. This cannot be undone."
        }
        confirmText={
          confirmDelete.kind === "export-html"
            ? "Export HTML"
            : confirmDelete.kind === "faqAll"
              ? "Delete all"
              : "Delete"
        }
        confirmButtonClass={
          confirmDelete.kind === "export-html" ? "btn-primary" : "btn-danger"
        }
        icon={
          confirmDelete.kind === "export-html"
            ? "fas fa-exclamation-triangle text-warning"
            : "fas fa-trash text-danger"
        }
      />

      <InfoAlert {...alertState} onClose={hideAlert} />
    </>
  );
}

export default ManageFaqModal;
